<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FileMenu from './FileMenu.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';

/* Ein fester Dokumentenplatz — Reisepass, Geburtsurkunde,
   Führungszeugnis. Anders als die freien Gruppen gibt es hier je Spieler
   genau einen Eintrag, der ersetzt statt ergänzt wird. */
const props = defineProps({
  player: { type: Object, required: true },
  slotId: { type: String, required: true },
  label: { type: String, required: true },
});

const { can } = useAuth();
const { confirm } = useConfirm();
const { t } = useI18n();
const { upload, remove, busy, error } = useFileActions();
const input = ref(null);

const doc = computed(() => props.player.identityDocs?.[props.slotId] || null);
const filled = computed(() => !!doc.value);
const canEdit = computed(() => can('documents', 'write'));

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  /* Beim Ersetzen verschwindet die bisherige Datei endgültig aus dem Speicher.
     Wer versehentlich die falsche Datei wählt, hätte sonst den alten Pass-Scan
     verloren, ohne gefragt worden zu sein. */
  if (doc.value) {
    const ok = await confirm({
      title: t('confirm.replaceSlotTitle'),
      message: t('confirm.removeSlot', { name: props.label }),
      confirmLabel: t('files.replace'),
    });
    if (!ok) return;
  }

  try {
    const stored = await upload(file, `identity/${props.player.id}`);
    /* Vorherige Datei erst nach erfolgreichem Hochladen entfernen — sonst
       steht man bei einem Fehler ohne beide da. */
    const previous = doc.value;
    if (!props.player.identityDocs) props.player.identityDocs = {};
    props.player.identityDocs[props.slotId] = stored;
    if (previous) await remove(previous);
  } catch {
    /* Meldung steht in error */
  }
}

async function onRemove() {
  const previous = doc.value;
  delete props.player.identityDocs[props.slotId];
  if (previous) await remove(previous);
}
</script>

<template>
  <div class="doc-slot" :class="{ filled }">
    <span class="ds-check">{{ filled ? '✓' : '' }}</span>

    <div class="ds-main">
      <div class="ds-label">{{ label }}</div>
      <div class="ds-file" :class="filled ? 'ok' : 'warn'">
        {{ filled ? $t('files.onFile') : $t('files.notUploaded') }}
      </div>
      <div v-if="error" class="ds-error">{{ $t(`errors.${error}`) }}</div>
    </div>

    <FileMenu
      v-if="filled"
      :file="doc"
      :name="label"
      area="documents"
      @remove="onRemove"
    />

    <template v-if="canEdit">
      <button class="file-label" :disabled="busy" @click="input.click()">
        {{ busy ? $t('common.loading') : filled ? $t('files.replace') : $t('files.upload') }}
      </button>
      <input
        ref="input"
        type="file"
        accept="application/pdf,image/*"
        hidden
        @change="onPick"
      />
    </template>
    <span v-else-if="!filled" class="non-admin-hint">{{ $t('files.uploadRestricted') }}</span>
  </div>
</template>

<style scoped>
.ds-main {
  flex: 1;
  min-width: 0;
}
.ds-error {
  font-size: var(--fs-xs);
  color: var(--red);
  margin-top: 3px;
}
.file-label {
  background: none;
  font: inherit;
}
.file-label:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
